import type { Component } from 'solid-js'
import { For, Show } from 'solid-js'
import { useNumberFormatter } from '../../shared/helpers/currency-formatter'
import { Trans, useTransContext } from '@mbarzda/solid-i18next'
import { Icon } from '@iconify-icon/solid'
import type { PlanCount } from '../../features/dashboard/domain/models/dashboard'
import Tooltip from '../shared/components/Tooltip'
import { useUser } from '../../shared/providers/user-provider'

export interface CardInfo {
  titleKey: string
  rows: { labelKey?: string; textKey: string }[]
  footerKey?: string
}

// Info icon next to a card title, styled like the chart tooltips. Texts get the artist name as
// {{artist}}.
const InfoTooltip: Component<{ info: CardInfo }> = (props) => {
  const [t] = useTransContext()
  const { currentOrganization } = useUser()
  const tr = (key: string) => t(key, { artist: currentOrganization()?.organization_name ?? '' })

  return (
    <Tooltip
      variant="light"
      content={
        <div class="w-72 whitespace-normal text-left text-xs">
          <div class="border-b border-gray-200 bg-gray-50 px-3 py-2 font-semibold">{tr(props.info.titleKey)}</div>
          <For each={props.info.rows}>
            {(row) => (
              <p class="px-3 py-1.5 font-normal leading-relaxed text-gray-500">
                <Show when={row.labelKey}>
                  <span class="font-semibold text-gray-900">{tr(row.labelKey!)}: </span>
                </Show>
                {tr(row.textKey)}
              </p>
            )}
          </For>
          <Show when={props.info.footerKey}>
            <p class="border-t border-gray-200 px-3 py-1.5 font-normal text-gray-500">{tr(props.info.footerKey!)}</p>
          </Show>
        </div>
      }
    >
      <span class="cursor-help text-gray-400 hover:text-gray-600" aria-label="info" data-testid="info-tooltip">
        <Icon icon="heroicons:information-circle" class="text-[0.875rem]" />
      </span>
    </Tooltip>
  )
}

interface BreakdownItem {
  labelKey?: string
  label?: string
  value: number
}

const DashboardUserSummaryCard: Component<{
  titleKey: string
  value: number
  info?: CardInfo
}> = (props) => {
  const { numberFormatter } = useNumberFormatter()

  return (
    <div class="p-2.5 bg-white rounded-lg shadow-sm border border-gray-200 flex flex-col justify-center items-center gap-0.5 md:flex-1 md:h-20 md:p-4 md:gap-1">
      <div class="text-center text-gray-900 text-base font-bold leading-6 md:text-xl md:leading-7">
        {numberFormatter(props.value) ?? '0'}
      </div>
      <div class="text-center text-gray-500 text-xs w-full font-semibold leading-4 whitespace-nowrap truncate text-ellipsis flex items-center justify-center gap-1">
        <Trans key={props.titleKey} />
        <Show when={props.info}>
          <InfoTooltip info={props.info!} />
        </Show>
      </div>
    </div>
  )
}

const BreakdownCard: Component<{
  titleKey: string
  total: number
  items: BreakdownItem[]
  info?: CardInfo
}> = (props) => {
  const { numberFormatter } = useNumberFormatter()

  return (
    <div class="relative group md:flex-1">
      <div class="p-2.5 bg-white rounded-lg shadow-sm border border-gray-200 flex flex-col justify-center items-center gap-0.5 md:h-20 md:p-4 md:gap-1 cursor-pointer">
        <div class="text-center text-gray-900 text-base font-bold leading-6 md:text-xl md:leading-7">
          {numberFormatter(props.total) ?? '0'}
        </div>
        <div class="text-center text-gray-500 text-xs w-full font-semibold leading-4 whitespace-nowrap truncate text-ellipsis flex items-center justify-center gap-1">
          <Trans key={props.titleKey} />
          <Show when={props.info}>
            <InfoTooltip info={props.info!} />
          </Show>
          <span class="text-gray-400 transition-transform duration-200 group-hover:rotate-180">
            <Icon icon="heroicons:chevron-down-20-solid" class="text-[0.75rem]" />
          </span>
        </div>
      </div>
      <div class="absolute left-0 right-0 top-full pt-2 z-30 opacity-0 -translate-y-1 pointer-events-none transition-all duration-200 ease-in-out group-hover:opacity-100 group-hover:translate-y-0 group-hover:pointer-events-auto">
        <div class="bg-white rounded-lg shadow-md border border-gray-200">
          <div class="flex items-stretch px-2 py-2.5">
            <For each={props.items}>
              {(item, index) => (
                <>
                  <Show when={index() > 0}>
                    <div class="w-px bg-gray-100 mx-2" />
                  </Show>
                  <div class="flex-1 flex flex-col items-center justify-center text-center gap-0.5 px-2">
                    <span class="text-sm font-bold text-gray-900">{numberFormatter(item.value) ?? '0'}</span>
                    <span class="text-[10px] font-medium text-gray-500 leading-tight">
                      <Show when={item.labelKey} fallback={item.label}>
                        <Trans key={item.labelKey!} />
                      </Show>
                    </span>
                  </div>
                </>
              )}
            </For>
          </div>
        </div>
      </div>
    </div>
  )
}

const DashboardUsersSummary: Component<{
  totalUsers: number
  paidUsers: number
  freeUsers: number
  freeSubscribers: number
  churnedUsers: number
  incompleteSignups: number
  totalMembers?: number
  activeMembers?: number
  inactiveMembers?: number
  paidPlans: PlanCount[]
}> = (props) => {
  // Only plans with subscribers are meaningful; a lone non-zero plan collapses to a plain card.
  const activePaidPlans = () => props.paidPlans.filter((p) => p.count > 0)

  const totalUsersInfo: CardInfo = {
    titleKey: 'ls_dashboard_total_users',
    rows: [
      { textKey: 'ls_dashboard_total_users_info' },
      { labelKey: 'ls_dashboard_active_members', textKey: 'ls_dashboard_total_users_info_active' },
      { labelKey: 'ls_dashboard_inactive_members', textKey: 'ls_dashboard_total_users_info_inactive' }
    ],
    footerKey: 'ls_dashboard_total_users_info_activity'
  }
  const paidUsersInfo: CardInfo = {
    titleKey: 'ls_dashboard_paid_users',
    rows: [{ textKey: 'ls_dashboard_paid_users_info' }]
  }
  const freeUsersInfo: CardInfo = {
    titleKey: 'ls_dashboard_free_users',
    rows: [
      { textKey: 'ls_dashboard_free_users_info' },
      { labelKey: 'ls_dashboard_free_subscribers', textKey: 'ls_dashboard_free_users_info_free_subscribers' },
      { labelKey: 'ls_dashboard_churned_users', textKey: 'ls_dashboard_free_users_info_churned' },
      { labelKey: 'ls_dashboard_incomplete_signups', textKey: 'ls_dashboard_free_users_info_incomplete' }
    ],
    footerKey: 'ls_dashboard_free_users_info_excluded'
  }

  return (
    <div class="flex gap-3 overflow-x-auto snap-x snap-mandatory pb-2 [&>div]:min-w-[120px] [&>div]:snap-start [&>div]:shrink-0 md:grid md:grid-cols-3 md:gap-6 md:overflow-visible md:pb-0 md:snap-none">
      <Show
        when={
          props.totalMembers !== undefined && props.activeMembers !== undefined && props.inactiveMembers !== undefined
        }
        fallback={<DashboardUserSummaryCard titleKey="ls_dashboard_total_users" value={props.totalUsers} />}
      >
        <BreakdownCard
          titleKey="ls_dashboard_total_users"
          total={props.totalMembers!}
          info={totalUsersInfo}
          items={[
            { labelKey: 'ls_dashboard_active_members', value: props.activeMembers! },
            { labelKey: 'ls_dashboard_inactive_members', value: props.inactiveMembers! }
          ]}
        />
      </Show>
      <Show
        when={activePaidPlans().length > 1}
        fallback={
          <DashboardUserSummaryCard titleKey="ls_dashboard_paid_users" value={props.paidUsers} info={paidUsersInfo} />
        }
      >
        <BreakdownCard
          titleKey="ls_dashboard_paid_users"
          total={props.paidUsers}
          info={paidUsersInfo}
          items={activePaidPlans().map((p) => ({ label: p.plan, value: p.count }))}
        />
      </Show>
      <BreakdownCard
        titleKey="ls_dashboard_free_users"
        total={props.freeSubscribers + props.churnedUsers + props.incompleteSignups}
        info={freeUsersInfo}
        items={[
          { labelKey: 'ls_dashboard_free_subscribers', value: props.freeSubscribers },
          { labelKey: 'ls_dashboard_churned_users', value: props.churnedUsers },
          { labelKey: 'ls_dashboard_incomplete_signups', value: props.incompleteSignups }
        ]}
      />
    </div>
  )
}

export default DashboardUsersSummary
